<template>
  <div :class="$style.container">
    <HgInput
      ref="inputRef"
      v-model="inputText"
      name="location"
      :label="noLabel ? '' : t('searchFilters.searchLabel')"
      :placeholder="t('searchFilters.placeholder')"
      :disabled="disabled"
      :allowClear="allowClear"
      autocomplete="off"
      :class="[$style.styledInput, { [$style.isDropdownVisible]: isDropdownVisible }]"
      updateModelOnInput
      :data-dd-action-name="DDActionName.SearchInput"
      @update:modelValue="handleInputChange"
      @focusin="handleFocus"
      @keyup.enter="handleKeyEnter"
      @keydown.up="handleKeyUp"
      @keydown.down="handleKeyDown"
    >
    </HgInput>
    <HgIcon
      v-if="allowClear && selectedLocation"
      icon="Cross"
      :class="$style.crossIcon"
      theme="gray"
      data-testid="cross-icon"
      :size="isIs24 ? 'small' : 'medium'"
      :rotation="isIs24 ? 270 : 0"
      :cursor="'pointer'"
      @click="clearSearch"
    />
    <HgIcon
      v-else
      icon="Search"
      :class="$style.searchIcon"
      data-testid="search-icon"
      theme="gray"
      :size="isIs24 ? 'small' : 'medium'"
      :rotation="isIs24 ? 270 : 0"
    />
    <ul
      v-if="isDropdownVisible"
      :class="[
        $style.dropdownWrapper,
        {
          [$style.isIs24]: isIs24,
          [$style.noLabel]: noLabel,
          [$style.mediumLayer]: dropdownLayerSize === 'medium',
          [$style.smallLayer]: dropdownLayerSize === 'small',
        },
      ]"
    >
      <li
        v-for="(location, i) in searchResultsWithoutSelected"
        :key="location.geoLocation.id"
        :class="[$style.dropdownItem, { [$style.isActive]: i === cursor }]"
        @mouseover="cursor = i"
        @click="handleSelectLocation(location.geoLocation)"
      >
        <span v-html="getHighlightedMatch(location)" />
        <span>{{ locationTypeLabel(location.geoLocation) }}</span>
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
import { HgIcon, HgInput, HgSupportedLanguage } from '@smg-real-estate/domus';
import { type GeoLocation } from '@smg-real-estate/geo-libs';
import { type GeoSearchResultItem } from '@smg-real-estate/geo-libs/dist/types/geo-api-responses';
import debounce from 'lodash.debounce';
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { CustomAction, DDActionName } from '@/helpers/datadogRUMService';
import { getDistinguishedDisplayValue } from '@/helpers/geo/geoLocationHelper';
import { getHighlightedDisplayValue } from '@/helpers/geo/geoSearchResultHelper';
import { isPlatformIs24, isServer } from '@/helpers/platform';
import { MIN_SEARCH_TEXT_LENGTH } from '@/types';
import { captureAction } from '@smg-real-estate/browser-rum';

interface Props {
  searchResults: Array<GeoSearchResultItem>;
  selectedLocation: GeoLocation | null;
  disabled?: boolean;
  allowClear?: boolean;
  noLabel?: boolean;
  dropdownLayerSize?: 'small' | 'medium';
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  allowClear: false,
  noLabel: false,
  dropdownLayerSize: 'medium',
});

const { t, locale } = useI18n();
const isIs24 = isPlatformIs24();

const cursor = ref<number>(-1);
const inputRef = ref<HTMLDivElement | null>(null);
const scopedSearchResults = ref<Array<GeoSearchResultItem>>(props.searchResults);

watch(
  () => props.searchResults,
  (newSearchResults) => {
    scopedSearchResults.value = newSearchResults;
  },
  { deep: true },
);

watch(
  () => props.selectedLocation,
  (value) => {
    inputText.value = getGeoLocationLabel(value);
  },
  { deep: true },
);

watch(locale, () => {
  inputText.value = getGeoLocationLabel(props.selectedLocation);
});

const searchResultsWithoutSelected = computed<Array<GeoSearchResultItem>>(() =>
  scopedSearchResults.value.filter(
    (location) => props.selectedLocation?.id !== location.geoLocation.id,
  ),
);
const isDropdownVisible = computed<boolean>(() => {
  const isInputActive = isServer()
    ? false
    : (inputRef.value as any)?.$el.contains(document.activeElement);
  return (
    inputText.value.length >= MIN_SEARCH_TEXT_LENGTH &&
    searchResultsWithoutSelected.value.length > 0 &&
    isInputActive
  );
});

const emit = defineEmits<{
  // TODO find the lint rule to match the Composition API
  /* eslint-disable unused-imports/no-unused-vars */
  (e: 'onSelectedLocationChange', location: GeoLocation): void;
  (e: 'onInputChange', value: string): void;
  (e: 'onClearLocation'): void;
}>();

const getGeoLocationLabel = (location: GeoLocation | null): string =>
  location
    ? getDistinguishedDisplayValue(location, locale.value as HgSupportedLanguage, {
        inclAllNames: true,
      })
    : '';

const defaultInputText = getGeoLocationLabel(props.selectedLocation);
const inputText = ref<string>(defaultInputText);

const handleSelectLocation = (location: GeoLocation) => {
  inputText.value = getGeoLocationLabel(location);
  scopedSearchResults.value = [];
  nextTick(() => {
    const input = (inputRef.value as any)?.$el.querySelector('input');
    if (input) {
      input.blur();
    }
  });
  emit('onSelectedLocationChange', location);

  // TODO: temporary RUM tracking example to collect the data
  captureAction(CustomAction.SearchLocation, {
    geoId: location.id,
  });
};

const getHighlightedMatch = (geoSearchResultItem: GeoSearchResultItem) =>
  getHighlightedDisplayValue(geoSearchResultItem, locale.value as HgSupportedLanguage);

const locationTypeLabel = (geoLocation: GeoLocation): string =>
  geoLocation.typeLabel?.[locale.value as HgSupportedLanguage] ?? '';

const handleInputChange = debounce(() => {
  const searchText = inputText.value;

  if (props.selectedLocation) {
    const selectedText = getDistinguishedDisplayValue(
      props.selectedLocation,
      locale.value as HgSupportedLanguage,
    );
    if (selectedText === searchText) return;
  }

  if (searchText.length >= MIN_SEARCH_TEXT_LENGTH) {
    cursor.value = -1;
    emit('onInputChange', searchText);
  }
}, 200);

const handleFocus = () => {
  const input = (inputRef.value as any)?.$el.querySelector('input');
  if (input) {
    input.select();
  }
};

const handleKeyEnter = () => {
  if (searchResultsWithoutSelected.value[cursor.value]) {
    handleSelectLocation(searchResultsWithoutSelected.value[cursor.value].geoLocation);
  }
};

const handleKeyUp = () => {
  if (cursor.value > -1) {
    cursor.value--;
  }
};

const handleKeyDown = () => {
  if (cursor.value < searchResultsWithoutSelected.value.length - 1) {
    cursor.value++;
  }
};
const clearSearch = () => {
  emit('onClearLocation');
};
</script>

<style lang="scss" module>
@use '@/styles/variables' as *;

$input-area-height-hg: 68px;
$input-area-height-is24: 72px;
$dropdown-small-layer: 130px;
$dropdown-medium-layer: 280px;

.container {
  position: relative;

  > div[class*='HgFormFieldWrapper_noLabel'] {
    padding-top: 0;
  }
}

.styledInput {
  [class*='HgFormFieldWrapper_innerElement'] {
    color: $color-grey-100;
  }

  input {
    padding-right: $space-250;
  }

  &.isDropdownVisible {
    [class*='HgInput_wrapper'] {
      border-bottom-right-radius: 0;
      border-bottom-left-radius: 0;
    }
  }
}

.searchIcon {
  position: absolute;
  right: $space-75;
  bottom: $space-75;
  width: $space-100;
  height: $space-100;
}

.crossIcon {
  position: absolute;
  right: $space-75;
  bottom: $space-75;
  width: $space-100;
  height: $space-100;
}

.buttonIcon {
  position: absolute;
  right: $space-75;
  bottom: $space-200;
  width: $space-100;
  height: $space-100;
}

.dropdownWrapper {
  position: absolute;
  top: $input-area-height-hg;
  z-index: 10;
  width: calc(100% - 2px);
  padding-left: 0;
  margin: 0;
  overflow-y: auto;
  cursor: pointer;
  background-color: $color-white;
  border: 1px solid $color-grey-50;
  border-radius: 0 0 $border-radius $border-radius;
  box-shadow: $elevation-low;

  &.mediumLayer {
    height: $dropdown-medium-layer;
  }

  &.smallLayer {
    height: $dropdown-small-layer;
  }

  &.isVisible {
    z-index: 4;
    display: block;
  }

  &.isIs24 {
    top: $input-area-height-is24;
  }

  &.noLabel {
    top: calc($input-area-height-hg - $space-150);

    &.isIs24 {
      top: calc($input-area-height-is24 - $space-150);
    }
  }
}

.dropdownItem {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: $space-100;
  margin: 0;

  @include body-2;

  &.isActive {
    background-color: $color-blue-0;
  }

  span {
    display: block;
    color: $color-grey-90;

    &:last-child {
      color: $color-grey-70;

      @include body-3;
    }
  }
}
</style>
